(() => {
  'use strict';

  const storageKey = 'hivilux-lead-attribution:v1';
  const storageVersion = 1;
  const storageLifetime = 30 * 24 * 60 * 60 * 1000;
  const fieldMarker = 'data-hivilux-attribution-field';
  const sourceParameters = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'gbraid',
    'wbraid'
  ];
  const sourceLabels = {
    utm_source: 'UTM Source',
    utm_medium: 'UTM Medium',
    utm_campaign: 'UTM Campaign',
    utm_content: 'UTM Content',
    utm_term: 'UTM Term',
    gclid: 'GCLID',
    gbraid: 'GBRAID',
    wbraid: 'WBRAID'
  };
  let capturedSourceFingerprint = '';
  let privacyApiRequested = false;
  let syncQueued = false;

  const cleanValue = (value, maximumLength = 500) => {
    if (typeof value !== 'string') return '';
    return value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, maximumLength);
  };

  const getStorage = (name) => {
    try {
      return window[name];
    } catch (error) {
      return null;
    }
  };

  const removeStoredState = () => {
    ['localStorage', 'sessionStorage'].forEach((name) => {
      const storage = getStorage(name);
      if (!storage) return;
      try {
        storage.removeItem(storageKey);
      } catch (error) {}
    });
  };

  const hasSourceValues = (touch) =>
    Boolean(
      touch &&
        touch.values &&
        sourceParameters.some((parameter) => cleanValue(touch.values[parameter]))
    );

  const readStoredState = () => {
    for (const name of ['localStorage', 'sessionStorage']) {
      const storage = getStorage(name);
      if (!storage) continue;

      try {
        const rawState = storage.getItem(storageKey);
        if (!rawState) continue;

        const state = JSON.parse(rawState);
        const isValid =
          state &&
          state.version === storageVersion &&
          Number(state.expiresAt) > Date.now() &&
          hasSourceValues(state.first) &&
          hasSourceValues(state.last);

        if (isValid) return state;
        storage.removeItem(storageKey);
      } catch (error) {
        try {
          storage.removeItem(storageKey);
        } catch (storageError) {}
      }
    }

    return null;
  };

  const writeStoredState = (state) => {
    const serializedState = JSON.stringify(state);
    ['localStorage', 'sessionStorage'].forEach((name) => {
      const storage = getStorage(name);
      if (!storage) return;
      try {
        storage.setItem(storageKey, serializedState);
      } catch (error) {}
    });
  };

  const marketingAllowed = () => {
    try {
      const result = window.Shopify?.customerPrivacy?.marketingAllowed?.();
      return result === true ? true : result === false ? false : null;
    } catch (error) {
      return null;
    }
  };

  const getReferrerHost = () => {
    if (!document.referrer) return '';
    try {
      return cleanValue(new URL(document.referrer).hostname, 255);
    } catch (error) {
      return '';
    }
  };

  const readCurrentSource = () => {
    const url = new URL(window.location.href);
    const values = {};

    sourceParameters.forEach((parameter) => {
      const value = cleanValue(url.searchParams.get(parameter));
      if (value) values[parameter] = value;
    });

    if (!sourceParameters.some((parameter) => values[parameter])) return null;

    let sourceType = 'UTM-tagged visit';
    if (values.gclid) sourceType = 'Google Ads click ID (GCLID)';
    if (!values.gclid && (values.gbraid || values.wbraid)) {
      sourceType = 'Google Ads privacy-preserving click ID';
    }

    return {
      sourceType,
      capturedAt: new Date().toISOString(),
      landingPath: cleanValue(url.pathname, 1000) || '/',
      referrerHost: getReferrerHost(),
      locale: cleanValue(document.documentElement.lang, 35),
      values
    };
  };

  const sourceFingerprint = (touch) =>
    JSON.stringify({
      landingPath: touch?.landingPath || '',
      values: sourceParameters.map((parameter) => touch?.values?.[parameter] || '')
    });

  const captureCurrentSource = () => {
    if (marketingAllowed() !== true) return null;

    const currentSource = readCurrentSource();
    const existingState = readStoredState();
    if (!currentSource) return existingState;

    const currentFingerprint = sourceFingerprint(currentSource);
    if (existingState && capturedSourceFingerprint === currentFingerprint) return existingState;
    capturedSourceFingerprint = currentFingerprint;

    const nextState = {
      version: storageVersion,
      expiresAt: Date.now() + storageLifetime,
      first: existingState?.first || currentSource,
      last: currentSource
    };
    writeStoredState(nextState);
    return nextState;
  };

  const isContactForm = (form) => {
    if (!form || form.tagName !== 'FORM') return false;
    if (!form.querySelector('[name^="contact["]')) return false;

    const formType = form.querySelector('input[name="form_type"][value="contact"]');
    if (formType) return true;

    try {
      const actionPath = new URL(form.getAttribute('action') || '', window.location.origin)
        .pathname.replace(/\/+$/, '');
      return actionPath === '/contact' || actionPath.endsWith('/contact');
    } catch (error) {
      return false;
    }
  };

  const hasNamedField = (form, name) =>
    Array.from(form.elements).some((element) => element.name === name);

  const fieldValue = (form, name) => {
    const field = Array.from(form.elements).find((element) => element.name === name);
    return cleanValue(field?.value || '', 255).toLowerCase();
  };

  const formType = (form) => {
    const requestType = fieldValue(form, 'contact[Anfragetyp]');
    if (
      form.matches('.muster-contact-form') ||
      form.closest('#musterbestellung') ||
      requestType.includes('muster') ||
      hasNamedField(form, 'contact[Lieferadresse]')
    ) {
      return 'Sample request';
    }

    if (
      hasNamedField(form, 'contact[Gewünschte Maße]') ||
      hasNamedField(form, 'contact[Projektionstyp]') ||
      form.closest('#sonderanfertigung')
    ) {
      return 'Custom screen inquiry';
    }

    if (form.matches('.halo-ask-an-expert-form') || form.id.toLowerCase().includes('ask')) {
      return 'Ask an expert';
    }

    if (form.matches('.notifyMe-form') || form.id.toLowerCase().includes('notify')) {
      return 'Back-in-stock request';
    }

    return 'Contact form';
  };

  const removeAttributionFields = (form) => {
    form
      .querySelectorAll(`[${fieldMarker}="true"]`)
      .forEach((field) => field.remove());
  };

  const addHiddenField = (form, label, value) => {
    const cleanLabel = cleanValue(label, 120);
    const cleanFieldValue = cleanValue(value, 1000);
    if (!cleanLabel || !cleanFieldValue) return;

    const name = `contact[${cleanLabel}]`;
    if (hasNamedField(form, name)) return;

    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    field.value = cleanFieldValue;
    field.setAttribute(fieldMarker, 'true');
    form.appendChild(field);
  };

  const addTouchFields = (form, prefix, touch) => {
    addHiddenField(form, `Attribution ${prefix} Source Type`, touch.sourceType);
    addHiddenField(form, `Attribution ${prefix} Captured At`, touch.capturedAt);
    addHiddenField(form, `Attribution ${prefix} Landing Path`, touch.landingPath);
    addHiddenField(form, `Attribution ${prefix} Referrer Host`, touch.referrerHost);
    addHiddenField(form, `Attribution ${prefix} Locale`, touch.locale);

    sourceParameters.forEach((parameter) => {
      addHiddenField(
        form,
        `Attribution ${prefix} ${sourceLabels[parameter]}`,
        touch.values[parameter]
      );
    });
  };

  const syncForm = (form, state) => {
    removeAttributionFields(form);
    if (!state || marketingAllowed() !== true) return;

    addHiddenField(form, 'Attribution Form Type', formType(form));
    addHiddenField(form, 'Attribution Consent Basis', 'Shopify marketing consent allowed');
    addTouchFields(form, 'First Touch', state.first);
    addTouchFields(form, 'Last Touch', state.last);
  };

  const contactForms = () => Array.from(document.forms).filter(isContactForm);

  const removeAllAttributionFields = () => {
    contactForms().forEach(removeAttributionFields);
  };

  const syncAllForms = () => {
    const state = marketingAllowed() === true ? readStoredState() : null;
    contactForms().forEach((form) => syncForm(form, state));
  };

  const queueFormSync = () => {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      syncAllForms();
    });
  };

  const applyConsentState = () => {
    const consent = marketingAllowed();
    if (consent === true) {
      captureCurrentSource();
      syncAllForms();
      return;
    }

    if (consent === false) removeStoredState();
    removeAllAttributionFields();
  };

  const requestPrivacyApi = () => {
    if (window.Shopify?.customerPrivacy || privacyApiRequested) return;
    if (typeof window.Shopify?.loadFeatures !== 'function') return;

    privacyApiRequested = true;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      applyConsentState
    );
  };

  const observeDynamicForms = () => {
    if (!document.body || typeof MutationObserver !== 'function') return;

    const observer = new MutationObserver((mutations) => {
      const containsNewForm = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            !node.hasAttribute?.(fieldMarker) &&
            (node.matches?.('form') || node.querySelector?.('form'))
        )
      );

      if (containsNewForm) queueFormSync();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const start = () => {
    document.addEventListener('visitorConsentCollected', applyConsentState);
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!isContactForm(form)) return;

        if (marketingAllowed() === true) {
          const state = captureCurrentSource() || readStoredState();
          syncForm(form, state);
        } else {
          removeAttributionFields(form);
        }
      },
      true
    );
    window.addEventListener('storage', (event) => {
      if (event.key === storageKey) queueFormSync();
    });
    observeDynamicForms();
    applyConsentState();
    requestPrivacyApi();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
